// Article 4 initial review, part 3 (26 Oct 2026): the client's questionnaire.
//
// Tokenised client page, sent like the Estate Planning Review. The twelve questions come from the server with the
// form (backend/hub/article4/questionnaire.py is the authority), so this page renders whatever parts each question
// has: text, a choice, a date, a number, or a list of costs. Each answer is {answer: {part: value}, confidence,
// client_note}. Answers save when a field loses focus. Questions that ask for a document take an upload, or the
// client can say they will email it instead. Submit locks the form and is what lets the review start.

import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle, Plus, Save, Trash2, UploadCloud } from "lucide-react";
import TokenGuard from "../components/client-forms/TokenGuard";
import { FIRM_NAME } from "../config/constants";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const NAVY = "#1f2a5a";

// Plain words for the stored choice values.
export const CHOICE_WORDS = {
  england: "England", wales: "Wales",
  freehold: "I own the freehold", leaseholder: "I am a leaseholder",
  buying_exchanged: "I am buying it and have exchanged contracts",
  buying_not_exchanged: "I am buying it and have not exchanged yet", other: "Other",
  family_house: "A family home", small_hmo: "A shared house for 3 to 6 people",
  large_hmo: "A shared house for 7 or more people", empty: "Empty",
  yes: "Yes", no: "No",
  refused: "Refused", granted_with_conditions: "Granted, with conditions I cannot work with",
  granted: "Granted", not_decided: "Not decided yet",
};

const PART_LABELS = {
  address: "Address", postcode: "Postcode", country: "Is it in England or Wales?", interest: "Your interest",
  other: "If other, please say", council: "Council", use: "Used as now", first_sharers_date: "Date the first sharers moved in",
  how_shown: "How you can show it", occupants: "Number of people it was planned for", works: "Works planned or done",
  works_started: "Date works started", seen: "Have you seen it?", notice_date: "Date of the council's notice",
  in_force_date: "Date it takes effect", applied: "Have you applied?", application_date: "Date applied",
  reference: "Application reference", decision: "Decision", decision_date: "Date of the decision",
  valued: "Has a valuer looked at it?", details: "Details",
};

const field = { width: "100%", padding: 10, border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14,
  fontFamily: "inherit", boxSizing: "border-box" };
const label = { display: "block", fontSize: 13, fontWeight: 600, color: "#0f172a", margin: "10px 0 4px" };

function sizeText(n) {
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

function Part({ qid, name, kind, value, onChange, onBlur }) {
  const id = `a4-${qid}-${name}`;
  const common = { id, "data-testid": id, onBlur };
  if (kind.startsWith("choice:")) {
    const options = kind.split(":")[1].split(",");
    return (
      <select {...common} style={field} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose one</option>
        {options.map((o) => <option key={o} value={o}>{CHOICE_WORDS[o] || o}</option>)}
      </select>
    );
  }
  if (kind === "date") return <input {...common} type="date" style={field} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (kind === "int") return <input {...common} type="number" min="0" max="1000" style={field} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  if (kind === "text" && ["works", "how_shown", "details"].includes(name)) {
    return <textarea {...common} rows={3} style={field} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  return <input {...common} type="text" style={field} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

function Costs({ qid, rows, onChange, onBlur }) {
  const list = Array.isArray(rows) ? rows : [];
  const set = (i, k, v) => onChange(list.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div data-testid={`a4-${qid}-costs`}>
      {list.map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr auto", gap: 6, marginBottom: 6 }}>
          <input aria-label="What it was" placeholder="What it was" style={field} value={r.item || ""} onChange={(e) => set(i, "item", e.target.value)} onBlur={onBlur} />
          <input aria-label="Paid to" placeholder="Paid to" style={field} value={r.paid_to || ""} onChange={(e) => set(i, "paid_to", e.target.value)} onBlur={onBlur} />
          <input aria-label="Amount in pounds" placeholder="£" inputMode="decimal" style={field} value={r.amount || ""} onChange={(e) => set(i, "amount", e.target.value)} onBlur={onBlur} />
          <input aria-label="Date paid" type="date" style={field} value={r.date || ""} onChange={(e) => set(i, "date", e.target.value)} onBlur={onBlur} />
          <button type="button" aria-label="Remove this cost" title="Remove" onClick={() => { const next = list.filter((_, j) => j !== i); onChange(next); onBlur(next); }}
            style={{ border: "none", background: "none", cursor: "pointer", color: "#64748b" }}><Trash2 size={16} /></button>
        </div>
      ))}
      <button type="button" data-testid={`a4-${qid}-add-cost`} onClick={() => onChange([...list, { item: "", paid_to: "", amount: "", date: "" }])}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, border: `1px solid ${NAVY}`, color: NAVY, background: "#fff", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 13 }}>
        <Plus size={14} /> Add a cost
      </button>
    </div>
  );
}

function Evidence({ question, uploads, forwarded, status, accept, acceptWords, onUpload, onForward }) {
  return (
    <div style={{ marginTop: 12, padding: 12, background: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: 6 }}>
      <div style={{ fontSize: 13, color: "#334155", marginBottom: 6 }}>Documents we would like: {question.evidence}</div>
      {uploads.length > 0 && (
        <ul style={{ margin: "0 0 8px", paddingLeft: 18, fontSize: 13 }}>
          {uploads.map((u) => <li key={u.upload_id}>{u.filename} ({sizeText(u.size_bytes || 0)})</li>)}
        </ul>
      )}
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", color: NAVY, fontSize: 13, fontWeight: 600 }}>
        <UploadCloud size={16} /> Attach a file
        <input type="file" accept={accept} data-testid={`a4-upload-${question.id}`} style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) onUpload(f); }} />
      </label>
      <span style={{ fontSize: 12, color: "#64748b", marginLeft: 8 }}>{acceptWords}, up to 20 MB</span>
      {status?.busy && <div aria-live="polite" style={{ fontSize: 13, color: "#475569", marginTop: 6 }}>Attaching...</div>}
      {status?.error && <div role="alert" style={{ fontSize: 13, color: "#991b1b", marginTop: 6 }}>{status.error}</div>}
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginTop: 8, color: "#334155" }}>
        <input type="checkbox" data-testid={`a4-forward-${question.id}`} checked={!!forwarded} onChange={(e) => onForward(e.target.checked)} />
        I will email this to you instead
      </label>
    </div>
  );
}

export default function ClientArticle4Questionnaire() {
  const { form_instance_id: formId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loadError, setLoadError] = useState("");
  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveError, setSaveError] = useState("");
  const [uploadStatus, setUploadStatus] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const headers = { "X-Form-Token": token };

  const load = useCallback(async () => {
    const resp = await fetch(`${API_BASE}/api/article4/questionnaire/${formId}`, { headers });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(typeof data.detail === "string" ? data.detail : "We could not open your questionnaire.");
    return data;
  }, [formId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) return;
    load().then((d) => { setForm(d); setAnswers(d.answers || {}); }).catch((e) => setLoadError(e.message));
  }, [load, token]);

  const setPart = (qid, part, value) => setAnswers((prev) => {
    const cur = prev[qid] || { answer: {} };
    return { ...prev, [qid]: { ...cur, answer: { ...(cur.answer || {}), [part]: value } } };
  });
  const setMeta = (qid, key, value) => setAnswers((prev) => ({ ...prev, [qid]: { answer: {}, ...(prev[qid] || {}), [key]: value } }));

  const save = useCallback(async (qid, override) => {
    const payload = override || answers[qid];
    if (!payload) return;
    setSaveStatus("saving");
    try {
      const resp = await fetch(`${API_BASE}/api/article4/questionnaire/${formId}`, {
        method: "PATCH", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ answers: { [qid]: payload } }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setSaveStatus("error");
        setSaveError(data?.detail?.message || (typeof data.detail === "string" ? data.detail : "That answer could not be saved."));
        return;
      }
      setSaveStatus("saved");
      setSaveError("");
    } catch (_e) {
      setSaveStatus("error");
      setSaveError("We could not save just now. Please check your connection; your answer is still on this page.");
    }
  }, [answers, formId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshUploads = async () => {
    const d = await load();
    setForm((prev) => ({ ...prev, uploads: d.uploads || [], evidence_forwarding: d.evidence_forwarding || {} }));
  };

  const upload = async (qid, file) => {
    setUploadStatus((s) => ({ ...s, [qid]: { busy: true } }));
    const body = new FormData();
    body.append("question_id", qid);
    body.append("file", file);
    try {
      const resp = await fetch(`${API_BASE}/api/article4/questionnaire/${formId}/upload`, { method: "POST", headers, body });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setUploadStatus((s) => ({ ...s, [qid]: { error: data?.detail?.message || "That file could not be attached." } }));
        return;
      }
      setUploadStatus((s) => ({ ...s, [qid]: {} }));
      await refreshUploads();
    } catch (_e) {
      setUploadStatus((s) => ({ ...s, [qid]: { error: "That file could not be attached. Please try again." } }));
    }
  };

  const forward = async (qid, on) => {
    const resp = await fetch(`${API_BASE}/api/article4/questionnaire/${formId}/evidence-forwarding`, {
      method: "PUT", headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ question_id: qid, forward_by_email: on }),
    });
    if (resp.ok) await refreshUploads();
    else setUploadStatus((s) => ({ ...s, [qid]: { error: "We could not record that just now. Please try again." } }));
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const resp = await fetch(`${API_BASE}/api/article4/questionnaire/${formId}/submit`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify({ confirm: true }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setSubmitError(typeof data.detail === "string" ? data.detail : "We could not send your questionnaire. Please try again.");
        return;
      }
      setForm((prev) => ({ ...prev, state: "submitted", submitted_at: data.submitted_at }));
    } catch (_e) {
      setSubmitError("We could not send your questionnaire. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const page = (children) => (
    <div style={{ maxWidth: 820, margin: "24px auto 64px", padding: "0 16px", fontFamily: "Calibri, Arial, sans-serif", color: "#0f172a" }}>
      <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>{FIRM_NAME} · Article 4 questionnaire</div>
      {children}
    </div>
  );

  if (!token) return <TokenGuard token={token} />;
  if (loadError) return page(<div role="alert" data-testid="a4-load-error" style={{ marginTop: 24, color: "#991b1b" }}>{loadError}</div>);
  if (!form) return page(<div style={{ marginTop: 24 }}>Loading your questionnaire...</div>);
  if (form.state === "submitted") {
    return page(
      <div data-testid="a4-submitted" style={{ marginTop: 32, padding: 24, border: "1px solid #e2e8f0", borderRadius: 8 }}>
        <CheckCircle size={28} color={NAVY} />
        <h1 style={{ fontSize: 22, color: NAVY }}>Thank you, we have your answers</h1>
        <p>Sent {String(form.submitted_at || "").slice(0, 10)}. We will be in touch to arrange your call. If anything changes, just reply to our email.</p>
      </div>,
    );
  }

  const uploads = form.uploads || [];
  const fwd = form.evidence_forwarding || {};
  return page(
    <>
      <h1 style={{ fontSize: 26, color: NAVY, margin: "8px 0 6px" }}>Your Article 4 questionnaire</h1>
      <p style={{ fontSize: 15, lineHeight: 1.6, color: "#334155" }}>
        {form.client_name ? `Thank you, ${form.client_name}. ` : "Thank you. "}
        Please answer what you can. If you are not sure of something, give your best answer and tick "not sure".
        Your answers save as you go, so you can come back to this link at any time within 30 days.
      </p>
      <div aria-live="polite" data-testid="a4-save-status" style={{ fontSize: 13, color: saveStatus === "error" ? "#b91c1c" : "#475569", margin: "8px 0 16px" }}>
        <Save size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
        {saveStatus === "saving" && "Saving..."}
        {saveStatus === "saved" && "Saved"}
        {saveStatus === "error" && saveError}
        {saveStatus === "idle" && "Your answers save automatically."}
      </div>

      {(form.questions || []).map((q, i) => {
        const a = answers[q.id] || { answer: {} };
        const parts = Object.entries(q.parts || {});
        return (
          <section key={q.id} data-testid={`a4-question-${q.id}`} style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, marginBottom: 14 }}>
            <h2 style={{ fontSize: 16, color: NAVY, margin: "0 0 4px" }}>{i + 1}. {q.prompt}</h2>
            {parts.map(([name, kind]) => (
              <div key={name}>
                {kind !== "costs" && parts.length > 1 && <label style={label} htmlFor={`a4-${q.id}-${name}`}>{PART_LABELS[name] || name}</label>}
                {kind === "costs"
                  ? <Costs qid={q.id} rows={(a.answer || {})[name]} onChange={(v) => setPart(q.id, name, v)}
                      onBlur={(next) => save(q.id, Array.isArray(next) ? { ...a, answer: { ...(a.answer || {}), [name]: next } } : undefined)} />
                  : <Part qid={q.id} name={name} kind={kind} value={(a.answer || {})[name]}
                      onChange={(v) => setPart(q.id, name, v)} onBlur={() => save(q.id)} />}
              </div>
            ))}
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginTop: 10, color: "#334155" }}>
              <input type="checkbox" data-testid={`a4-notsure-${q.id}`} checked={a.confidence === "not_sure"}
                onChange={(e) => { const next = { ...a, confidence: e.target.checked ? "not_sure" : "sure" }; setMeta(q.id, "confidence", next.confidence); save(q.id, next); }} />
              I am not sure about this answer
            </label>
            <label style={label} htmlFor={`a4-${q.id}-note`}>Anything you want to add (optional)</label>
            <textarea id={`a4-${q.id}-note`} rows={2} style={field} value={a.client_note || ""}
              onChange={(e) => setMeta(q.id, "client_note", e.target.value)} onBlur={() => save(q.id)} />
            {q.evidence && (
              <Evidence question={q} uploads={uploads.filter((u) => u.question_id === q.id)} forwarded={fwd[q.id]?.forward_by_email}
                status={uploadStatus[q.id]} accept={form.accept} acceptWords={form.accept_words}
                onUpload={(f) => upload(q.id, f)} onForward={(on) => forward(q.id, on)} />
            )}
          </section>
        );
      })}

      <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 16, marginTop: 8 }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 14 }}>
          <input type="checkbox" data-testid="a4-confirm" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} style={{ marginTop: 3 }} />
          My answers are correct to the best of my knowledge. I understand I cannot change them on this page once sent.
        </label>
        {submitError && <div role="alert" style={{ color: "#991b1b", fontSize: 13, marginTop: 8 }}>{submitError}</div>}
        <button type="button" data-testid="a4-submit" disabled={!confirm || submitting} onClick={submit}
          style={{ marginTop: 12, background: confirm ? NAVY : "#94a3b8", color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 15, cursor: confirm ? "pointer" : "not-allowed" }}>
          {submitting ? "Sending..." : "Send my answers"}
        </button>
        {form.client_care_email && (
          <p style={{ fontSize: 13, color: "#64748b", marginTop: 12 }}>
            Questions? Reply to our email or write to <a href={`mailto:${form.client_care_email}`}>{form.client_care_email}</a>.
          </p>
        )}
      </div>
    </>,
  );
}
